import { Tooltip } from 'components/ui/tooltip';
import { t } from 'utils/getTranslations';
import { isOnVacation, VACATION_MARKER_EMOJI } from 'utils/nameOrder';

interface VacationMarkerProps {
    onVacation?: boolean | null;
}

/**
 * Palm-tree marker with an "On a vacation" tooltip, shown before an on-vacation employee's name
 * (VACATION_DECLARER) wherever the name is rendered as JSX. Renders nothing when not on vacation.
 * String-only contexts (a dropdown's selected-value label) can't host a tooltip and instead get the
 * plain emoji from `vacationPrefix` in `utils/nameOrder`.
 */
const VacationMarker = ({ onVacation }: VacationMarkerProps): JSX.Element | null => {
    if (!onVacation) {
        return null;
    }
    return (
        <Tooltip content={t('employees.on-vacation')}>
            <span className="mr-1 cursor-default" role="img" aria-label={t('employees.on-vacation')}>
                {VACATION_MARKER_EMOJI}
            </span>
        </Tooltip>
    );
};

export default VacationMarker;

interface VacationOptionLabelProps {
    /** The dropdown option/entity — read for its `onVacation` flag. */
    option: unknown;
    /** The already-computed option label string (may carry the leading `vacationPrefix` emoji). */
    label: string | null;
}

/**
 * Renders a dropdown option's label, turning the leading palm-tree emoji (from `vacationPrefix`)
 * into a tooltip'd `<VacationMarker>` when the option is on vacation. Lets the base-select
 * `renderOption`s show the "On a vacation" tooltip in the option list while `getOptionLabel` keeps
 * returning the plain string the Autocomplete needs for the selected value and filtering.
 */
export const VacationOptionLabel = ({ option, label }: VacationOptionLabelProps): JSX.Element => {
    if (!isOnVacation(option)) {
        return <>{label}</>;
    }
    const prefix = `${VACATION_MARKER_EMOJI} `;
    const rest = label?.startsWith(prefix) ? label.slice(prefix.length) : label;
    return (
        <>
            <VacationMarker onVacation />
            {rest}
        </>
    );
};
